/**
 * NavBarLogo Component
 * 
 * Compact logo variant for navigation bars in user websites.
 * Features hover state with "Created with" / "Create with" animation.
 * Uses brand configuration for colors and domain name.
 * 
 * Usage:
 *   import { NavBarLogo } from '../design-system/brand';
 *   <NavBarLogo iconSize="w-4 h-4" textSize="text-xs" />
 */
import React, { useState } from "react";
import LogoIcon from "./LogoIcon";
import { brand } from "./brandConfig";
import { brandColors, interactive } from "../tokens";
import { logoFontStyle } from "./logoFont";

const NavBarLogo = ({ 
  iconSize = "w-4 h-4", 
  textSize = "text-xs", 
  iconColor = "#60a5fa",
  textColor = brandColors.gradientText,
  className = ""
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const badgeStateClassName = isHovered
    ? `${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText} border-transparent shadow-none`
    : "bg-gradient-to-r from-white via-blue-50 to-blue-100 border-blue-200 shadow-none";
  const domainTextClassName = isHovered ? "text-white" : textColor;

  return (
    <div 
      className={`rounded-[20px] px-3 py-2 transition-all duration-300 flex items-center space-x-1.5 whitespace-nowrap border ${badgeStateClassName} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo Icon on the left */}
      <div
        className={`transition-colors duration-200 ${isHovered ? "text-white" : ""}`}
        style={isHovered ? undefined : { color: iconColor }}
      >
        <LogoIcon
          className={`${iconSize} transition-colors duration-200`}
          color={iconColor}
          useCurrentColor={isHovered}
        />
      </div>
      
      {/* myvitae.ai text with integrated "Create with" */}
      <div className="relative">
        <span className={`${textSize} font-bold transition-colors duration-200 ${domainTextClassName}`} style={logoFontStyle}>
          {brand.identity.domain}<sup className="text-[8px]">®</sup>
        </span>
        <div className={`absolute -top-0.5 left-0 text-[6px] leading-none whitespace-nowrap transition-colors duration-200 ${isHovered ? "text-white" : "text-blue-600"}`}>
          {isHovered ? 'Create with' : 'Created with'}
        </div>
      </div>
    </div>
  );
};

export default NavBarLogo;
